/**
 * stk500.js — gravar a controladora pela USB, do navegador (Onda 141c)
 * CI Sistemas de Informática Ltda. — AcessoiD
 *
 * A Keyestudio KS0304 é um Arduino Uno: o bootloader Optiboot fala STK500 v1 pela serial a
 * 115200. O navegador (Chrome/Edge no computador) abre a porta pela Web Serial API, reinicia a
 * placa pelo DTR, sincroniza, grava o programa em páginas de 128 bytes, lê de volta para conferir
 * e sai do modo de programação. É o que o avrdude faz com `-c arduino`.
 *
 * O .hex vem do servidor (Intel HEX em texto). `parseIntelHex` e `montarPaginas` são puros, para
 * teste; `gravarPeloNavegador` é a parte que toca a porta.
 */

const INSYNC = 0x14;
const OK = 0x10;
const CRC_EOP = 0x20;
const PAGINA = 128;
const ASSINATURA_328P = [0x1e, 0x95, 0x0f];

export function suportaWebSerial() {
  return typeof navigator !== 'undefined' && !!navigator.serial && typeof navigator.serial.requestPort === 'function';
}

/** Intel HEX → { memoria: Uint8Array (preenchida com 0xFF), tamanho } */
export function parseIntelHex(texto) {
  const memoria = new Uint8Array(32768).fill(0xff);
  let tamanho = 0;
  let base = 0;
  for (const linhaBruta of String(texto).split(/\r?\n/)) {
    const linha = linhaBruta.trim();
    if (!linha.startsWith(':')) continue;
    const n = parseInt(linha.substr(1, 2), 16);
    const endereco = parseInt(linha.substr(3, 4), 16);
    const tipo = parseInt(linha.substr(7, 2), 16);
    if (tipo === 1) break;
    if (tipo === 2) { base = parseInt(linha.substr(9, 4), 16) << 4; continue; }
    if (tipo === 4) { base = parseInt(linha.substr(9, 4), 16) << 16; continue; }
    if (tipo !== 0) continue;
    let soma = 0;
    for (let i = 1; i < linha.length - 1; i += 2) soma = (soma + parseInt(linha.substr(i, 2), 16)) & 0xff;
    if (soma !== 0) throw new Error(`Soma de verificação errada no .hex (endereço ${endereco.toString(16)})`);
    for (let i = 0; i < n; i++) {
      const pos = base + endereco + i;
      if (pos >= memoria.length) throw new Error('O programa não cabe na memória do ATmega328P');
      memoria[pos] = parseInt(linha.substr(9 + 2 * i, 2), 16);
    }
    tamanho = Math.max(tamanho, base + endereco + n);
  }
  if (!tamanho) throw new Error('O .hex está vazio');
  return { memoria, tamanho };
}

/** As páginas a gravar: [{ enderecoByte, dados(128) }] */
export function montarPaginas(memoria, tamanho) {
  const paginas = [];
  for (let a = 0; a < tamanho; a += PAGINA) paginas.push({ enderecoByte: a, dados: memoria.slice(a, a + PAGINA) });
  return paginas;
}

/** Os bytes de cada comando do STK500 v1 — separados para o teste conferir o enquadramento. */
export const comando = {
  sync: () => Uint8Array.from([0x30, CRC_EOP]),
  assinatura: () => Uint8Array.from([0x75, CRC_EOP]),
  entrarProg: () => Uint8Array.from([0x50, CRC_EOP]),
  sairProg: () => Uint8Array.from([0x51, CRC_EOP]),
  endereco: (enderecoByte) => { const w = enderecoByte >> 1; return Uint8Array.from([0x55, w & 0xff, (w >> 8) & 0xff, CRC_EOP]); },
  gravarPagina: (dados) => Uint8Array.from([0x64, (dados.length >> 8) & 0xff, dados.length & 0xff, 0x46, ...dados, CRC_EOP]),
  lerPagina: (n) => Uint8Array.from([0x74, (n >> 8) & 0xff, n & 0xff, 0x46, CRC_EOP]),
};

/**
 * Executa o protocolo sobre um transporte { escrever(bytes), ler(n, timeoutMs), descartar() }.
 * Separado da Web Serial para o teste rodar sem porta.
 */
export async function programar(transporte, { memoria, tamanho }, onProgresso = () => {}) {
  const espera = async (n = 0, timeout = 2000) => {
    const r = await transporte.ler(n + 2, timeout);
    if (r[0] !== INSYNC || r[r.length - 1] !== OK) throw new Error('A placa respondeu fora de sincronia');
    return r.slice(1, 1 + n);
  };
  const cmd = async (bytes, n = 0, timeout = 2000) => { await transporte.escrever(bytes); return espera(n, timeout); };

  onProgresso({ etapa: 'sincronizando', pct: 0 });
  let sincronizado = false;
  for (let i = 0; i < 10 && !sincronizado; i++) {
    try { await cmd(comando.sync(), 0, 600); sincronizado = true; } catch (_) { await transporte.descartar(); }
  }
  if (!sincronizado) throw new Error('A placa não respondeu ao bootloader. Confira o cabo USB e se a porta certa foi escolhida; tente de novo logo após conectar.');

  const sig = await cmd(comando.assinatura(), 3);
  if (sig[0] !== ASSINATURA_328P[0] || sig[1] !== ASSINATURA_328P[1] || sig[2] !== ASSINATURA_328P[2]) {
    throw new Error(`A placa não é um ATmega328P (assinatura ${Array.from(sig).map(b => b.toString(16)).join(' ')})`);
  }
  await cmd(comando.entrarProg());

  const paginas = montarPaginas(memoria, tamanho);
  for (let i = 0; i < paginas.length; i++) {
    await cmd(comando.endereco(paginas[i].enderecoByte));
    await cmd(comando.gravarPagina(paginas[i].dados), 0, 4000);
    onProgresso({ etapa: 'gravando', pct: Math.round(((i + 1) / paginas.length) * 50) });
  }
  let diferentes = 0;
  for (let i = 0; i < paginas.length; i++) {
    await cmd(comando.endereco(paginas[i].enderecoByte));
    const lido = await cmd(comando.lerPagina(PAGINA), PAGINA, 4000);
    for (let j = 0; j < PAGINA; j++) if (lido[j] !== paginas[i].dados[j]) diferentes++;
    onProgresso({ etapa: 'conferindo', pct: 50 + Math.round(((i + 1) / paginas.length) * 50) });
  }
  await cmd(comando.sairProg());
  if (diferentes) throw new Error(`A conferência achou ${diferentes} byte(s) diferentes: grave de novo.`);
  onProgresso({ etapa: 'pronto', pct: 100 });
  return { paginas: paginas.length, bytes: tamanho };
}

/** Transporte sobre a Web Serial API. */
async function transporteWebSerial(porta) {
  await porta.open({ baudRate: 115200 });
  const escritor = porta.writable.getWriter();
  const leitor = porta.readable.getReader();
  let fila = new Uint8Array(0);
  let fechado = false;
  const bombear = (async () => {
    try {
      for (;;) {
        const { value, done } = await leitor.read();
        if (done) break;
        if (value && value.length) { const n = new Uint8Array(fila.length + value.length); n.set(fila); n.set(value, fila.length); fila = n; }
      }
    } catch (_) { /* porta fechada */ } finally { fechado = true; }
  })();
  const dormir = (ms) => new Promise(r => setTimeout(r, ms));
  return {
    porta,
    async reiniciar() {
      // Como o avrdude: DTR/RTS em baixo, 50 ms, em alto; o Optiboot espera o sync por ~1 s.
      await porta.setSignals({ dataTerminalReady: false, requestToSend: false });
      await dormir(50);
      await porta.setSignals({ dataTerminalReady: true, requestToSend: true });
      await dormir(250);
      fila = new Uint8Array(0);
    },
    async escrever(bytes) { await escritor.write(bytes); },
    async ler(n, timeoutMs) {
      const fim = Date.now() + timeoutMs;
      while (fila.length < n) {
        if (fechado) throw new Error('A porta fechou');
        if (Date.now() > fim) throw new Error('tempo esgotado');
        await dormir(5);
      }
      const r = fila.slice(0, n); fila = fila.slice(n); return r;
    },
    async descartar() { await dormir(100); fila = new Uint8Array(0); },
    async fechar() {
      try { await leitor.cancel(); } catch (_) { /* já cancelado */ }
      try { escritor.releaseLock(); } catch (_) { /* já solto */ }
      await bombear;
      try { await porta.close(); } catch (_) { /* já fechada */ }
    },
  };
}

/**
 * O fluxo inteiro no navegador: pede a porta, reinicia a placa, grava e confere.
 * @param {string} hexTexto  Intel HEX vindo do servidor
 * @param {(p:{etapa:string,pct:number})=>void} onProgresso
 */
export async function gravarPeloNavegador(hexTexto, onProgresso = () => {}) {
  if (!suportaWebSerial()) throw new Error('Este navegador não grava pela USB. Use o Chrome ou o Edge no computador.');
  const imagem = parseIntelHex(hexTexto);
  onProgresso({ etapa: 'escolha a porta', pct: 0 });
  // Filtro: a KS0304 se apresenta como Arduino Uno (2341:0043) ou pela ponte CH340 (1A86:7523).
  const porta = await navigator.serial.requestPort({ filters: [{ usbVendorId: 0x2341 }, { usbVendorId: 0x1a86 }, { usbVendorId: 0x0403 }, { usbVendorId: 0x10c4 }] });
  const t = await transporteWebSerial(porta);
  try {
    onProgresso({ etapa: 'reiniciando a placa', pct: 0 });
    await t.reiniciar();
    return await programar(t, imagem, onProgresso);
  } finally {
    await t.fechar();
  }
}
